<script setup lang="ts">
/**
 * CampaignDonationCard — carte latérale "desktop" : ring de progression +
 * montant collecté + CTAs (Je participe / Partager) + liste compacte des
 * donateurs (top 5) avec animations d'arrivée.
 *
 * Composant PARTAGÉ (@donoor/ui) consommé en SOURCE par dashboard.donoor.org
 * et donoor.org. Couleurs en hex en dur (pas de token de thème) → rendu
 * identique dans les 2 apps.
 *
 * Le composant ne connaît PAS la destination du don : il émet `donate` et
 * chaque page décide (public → naviguer, preview → simuler).
 *
 * `<UIcon>` reste global (résolu par l'app Nuxt qui transpile ce SFC via
 * build.transpile).
 *
 * USAGE (auto-import via wrapper local) :
 *   import CampaignDonationCard from "@donoor/ui/campaign/CampaignDonationCard.vue";
 *
 * Karim 2026-06-21 (factorisation cagnotte).
 */
import type { Campaign, CampaignDonor, TopDonor } from "./types";
import { useAnimatedNumber } from "@donoor/composables";
import CampaignCountdown from "./CampaignCountdown.vue";

// Internationalisation (lot "paquets partagés", 2026-10-11) : clés `ui.*`,
// présentes dans donoor.org ET dashboard.donoor.org (useI18n auto-importé).
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    campaign: Campaign;
    progressPercent: number;
    isCampaignClosed: boolean;
    isCampaignPaused: boolean;
    hasGoal: boolean;
    formatAmountShort: (amount: number) => string;
    getRelativeTime: (dateString: string) => string;
    showAmountRaised?: boolean;
    donors: CampaignDonor[];
    donorsTotal: number;
    topDonor: TopDonor | null;
    isLoadingDonors: boolean;
    formatAmountCompact?: (amount: number | null | undefined) => string;
    formatCountCompact?: (count: number | null | undefined) => string;
    /** Instant de clôture (ISO) : affiche la barre de temps. */
    closesAt?: string | null;
    /** « 8 octobre » — le jour de fin, pour la phrase sous le compte. */
    endDateLabel?: string | null;
  }>(),
  {
    showAmountRaised: true,
    formatAmountCompact: undefined,
    formatCountCompact: undefined,
    closesAt: null,
    endDateLabel: null,
  },
);

const emit = defineEmits<{
  openShare: [];
  seeAll: [];
  seeTop: [];
  donate: [];
  /** Le temps est écoulé : la cagnotte vient de se clôturer. */
  ended: [];
}>();

const { displayValue: animatedAmount, setValue: setAmount } = useAnimatedNumber(
  0,
  1400,
);
const { displayValue: animatedDonors, setValue: setDonors } = useAnimatedNumber(
  0,
  1200,
);
const { displayValue: animatedProgress, setValue: setProgress } =
  useAnimatedNumber(0, 1400);

const amountAnimating = ref(false);
const isFirstRender = ref(true);

watch(
  () => props.campaign.raised,
  (newVal, oldVal) => {
    const currentVal = newVal || 0;
    setAmount(currentVal);
    // Pulse uniquement après le premier rendu (sinon flash au mount)
    if (!isFirstRender.value && oldVal !== undefined && currentVal !== oldVal) {
      amountAnimating.value = true;
      setTimeout(() => {
        amountAnimating.value = false;
      }, 1000);
    }
  },
);

watch(
  () => props.campaign.donors_count,
  (newVal) => {
    setDonors(newVal || 0);
  },
);

watch(
  () => props.progressPercent,
  (newVal) => {
    setProgress(newVal || 0);
  },
);

onMounted(() => {
  // Animation d'entrée : 0 → valeurs finales
  setAmount(props.campaign.raised || 0);
  setDonors(props.campaign.donors_count || 0);
  setProgress(props.progressPercent || 0);
  nextTick(() => {
    isFirstRender.value = false;
  });
});

// Progress ring SVG
const RING_SIZE = 70;
const RING_STROKE = 9;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const ringDashOffset = computed(
  () =>
    RING_CIRCUMFERENCE - (animatedProgress.value / 100) * RING_CIRCUMFERENCE,
);

// Fallbacks locaux si les helpers ne sont pas passés en prop
function defaultAmountCompact(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1).replace(".0", "")}M €`;
  }
  if (n >= 1_000) {
    const k = n / 1_000;
    return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1).replace(".0", "")}k €`;
  }
  return `${Math.round(n)} €`;
}
function defaultCountCompact(count: number | null | undefined): string {
  if (count === null || count === undefined) return "0";
  const n = Number(count);
  if (!Number.isFinite(n)) return "0";
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return m % 1 === 0 ? `${m.toFixed(0)}M` : `${m.toFixed(1).replace(".0", "")}M`;
  }
  if (n >= 1_000) {
    const k = n / 1_000;
    return k % 1 === 0 ? `${k.toFixed(0)}k` : `${k.toFixed(1).replace(".0", "")}k`;
  }
  return `${Math.round(n)}`;
}
const amountCompact = (a: number | null | undefined) =>
  props.formatAmountCompact ? props.formatAmountCompact(a) : defaultAmountCompact(a);
const countCompact = (n: number | null | undefined) =>
  props.formatCountCompact ? props.formatCountCompact(n) : defaultCountCompact(n);

const visibleDonors = computed(() => props.donors.slice(0, 5));

// ─── Highlight "nouveau don" (3s) ────────────────────────────────────────
// Pattern identique à CampaignDonorsList.vue : on watch le 1er donateur,
// quand son id change c'est qu'un nouveau don est arrivé en tête de liste
// (les donors sont triés par date desc), on tag l'id pour 3s pour
// déclencher l'animation .new-donation-highlight + le ring scale-110.
const recentlyAddedIds = ref<Set<string>>(new Set());

watch(
  () => visibleDonors.value[0]?.id,
  (newFirstId, oldFirstId) => {
    if (newFirstId && newFirstId !== oldFirstId) {
      recentlyAddedIds.value.add(newFirstId);
      setTimeout(() => {
        recentlyAddedIds.value.delete(newFirstId);
      }, 3000);
    }
  },
);

function isRecentlyAdded(donorId: string): boolean {
  return recentlyAddedIds.value.has(donorId);
}

const recentDonationsCount = computed(() => {
  // Donations dans les dernières 24h
  const now = Date.now();
  const dayAgo = now - 24 * 60 * 60 * 1000;
  return props.donors.filter((d) => {
    const t = new Date(d.created_at).getTime();
    return t >= dayAgo;
  }).length;
});

function donorBadgeLabel(donor: CampaignDonor): string {
  if (donor.id === props.topDonor?.id) return t("ui.donationCard.topDonation");
  return props.getRelativeTime(donor.created_at);
}
</script>

<template>
  <div
    class="w-full min-w-0 bg-white rounded-[32px] border border-gray-200 shadow-sm p-6 lg:p-7"
  >
    <!-- Stats: ring + montant -->
    <div
      v-if="showAmountRaised && hasGoal"
      class="flex items-center gap-4 mb-5"
    >
      <!-- Progress ring -->
      <div class="relative shrink-0">
        <svg :width="RING_SIZE" :height="RING_SIZE" class="-rotate-90">
          <defs>
            <linearGradient
              id="progress-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stop-color="#1f9afe" />
              <stop offset="100%" stop-color="#1366B5" />
            </linearGradient>
          </defs>
          <circle
            :cx="RING_SIZE / 2"
            :cy="RING_SIZE / 2"
            :r="RING_RADIUS"
            stroke="#e5e7eb"
            :stroke-width="RING_STROKE"
            fill="none"
          />
          <circle
            :cx="RING_SIZE / 2"
            :cy="RING_SIZE / 2"
            :r="RING_RADIUS"
            stroke="url(#progress-gradient)"
            :stroke-width="RING_STROKE"
            fill="none"
            stroke-linecap="round"
            :stroke-dasharray="RING_CIRCUMFERENCE"
            :stroke-dashoffset="ringDashOffset"
            class="transition-all duration-700"
          />
        </svg>
        <span
          class="absolute inset-0 flex items-center justify-center text-sm text-gray-900 ultra-bold"
        >
          {{ Math.round(animatedProgress) }}%
        </span>
      </div>

      <div class="min-w-0">
        <p
          class="text-2xl text-gray-900 leading-tight transition-all ultra-bold"
          :class="{ 'amount-pulse': amountAnimating }"
        >
          <span>{{ formatAmountShort(animatedAmount) }}</span>
          <span class="ml-1">{{ t("ui.commun.collectes") }}</span>
        </p>
        <p class="text-2xl text-gray-500 leading-tight mt-0.5 font-medium">
          {{ t("ui.commun.sur") }} {{ amountCompact(campaign.goal_amount) }}
        </p>
        <p class="text-sm text-gray-500 mt-1">
          {{ countCompact(animatedDonors) }} {{ t("ui.commun.don", animatedDonors) }}
        </p>
      </div>
    </div>

    <!-- Cas sans objectif -->
    <div v-else-if="showAmountRaised" class="mb-5">
      <p class="text-2xl text-gray-900 leading-tight">
        <span
          class="font-black"
          style="font-weight: 1000; font-stretch: 110%"
          >{{ formatAmountShort(animatedAmount) }}</span
        >
      </p>
      <p class="text-sm text-gray-500">
        {{ t("ui.commun.collectes") }} · {{ animatedDonors }} {{ t("ui.commun.don", animatedDonors) }}
      </p>
    </div>

    <div v-else class="mb-5 flex items-center gap-2 text-gray-700">
      <UIcon name="i-lucide-users" class="size-5" />
      <span class="text-xl font-bold">{{ animatedDonors }}</span>
      <span class="text-sm text-gray-500">{{ t("ui.commun.participant", animatedDonors) }}</span>
    </div>

    <!-- Barre de temps (cagnotte en ligne, date de fin posée) -->
    <CampaignCountdown
      v-if="closesAt && endDateLabel && !isCampaignClosed && !isCampaignPaused"
      :closes-at="closesAt"
      :starts-at="campaign.start_date || campaign.date_created || campaign.created_at || null"
      :end-date-label="endDateLabel"
      :days-remaining="campaign.days_remaining ?? null"
      class="mb-5"
      @ended="emit('ended')"
    />

    <!-- Status messages -->
    <div
      v-if="isCampaignClosed"
      class="mb-4 py-3 px-4 rounded-xl bg-gray-100 flex items-center justify-center gap-2"
    >
      <UIcon name="i-lucide-lock" class="size-4 text-gray-500" />
      <span class="text-sm font-bold text-gray-600 uppercase tracking-wide">
        {{ t("ui.commun.cagnotteCloturee") }}
      </span>
    </div>
    <div
      v-else-if="isCampaignPaused"
      class="mb-4 py-3 px-4 rounded-xl bg-amber-50 flex items-center justify-center gap-2"
    >
      <UIcon name="i-lucide-pause-circle" class="size-4 text-amber-600" />
      <span class="text-sm font-bold text-amber-700 uppercase tracking-wide">
        {{ t("ui.commun.cagnotteEnPause") }}
      </span>
    </div>

    <!-- CTAs -->
    <template v-if="!isCampaignClosed && !isCampaignPaused">
      <button
        type="button"
        class="w-full py-3 rounded-full text-base font-bold bg-[#0F3D8C] hover:bg-[#0A2D6B] text-white transition-all shadow-sm hover:shadow-md"
        @click="emit('donate')"
      >
        {{ t("ui.commun.jeParticipe") }}
      </button>
      <button
        type="button"
        class="mt-3 w-full py-3 rounded-full text-base font-bold bg-[#D7EBFF] hover:bg-[#A8D2FE] text-[#0F3D8C] transition-all flex items-center justify-center gap-2"
        @click="emit('openShare')"
      >
        <UIcon name="i-lucide-share-2" class="size-4" />
        {{ t("commun.actions.partager") }}
      </button>
    </template>

    <!-- Recent donations indicator -->
    <div v-if="recentDonationsCount > 0" class="mt-6 flex items-center gap-2.5">
      <div
        class="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center shrink-0"
      >
        <UIcon name="i-lucide-trending-up" class="size-4 text-purple-600" />
      </div>
      <span class="font-semibold text-purple-700 text-sm">
        {{ t("ui.donationCard.donsRecents", { n: recentDonationsCount }, recentDonationsCount) }}
      </span>
    </div>

    <!-- Donors list (compact, top 5) -->
    <!-- TransitionGroup pour animer l'arrivée smooth d'un nouveau donateur :
         slide depuis le haut + fade + scale, les items existants se
         décalent en FLIP. Highlight bleu Donoor qui s'estompe sur le
         nouveau (cf. .new-donation-highlight ci-dessous). -->
    <TransitionGroup
      v-if="visibleDonors.length > 0"
      key="donors-list"
      name="donor-card"
      tag="div"
      class="mt-6 flex flex-col gap-5 w-full"
    >
      <div
        v-for="donor in visibleDonors"
        :key="donor.id"
        class="flex items-center gap-3 w-full min-w-0 rounded-lg transition-colors duration-500"
        :class="isRecentlyAdded(donor.id) ? 'new-donation-highlight' : ''"
      >
        <div
          class="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-600 shrink-0 transition-all duration-300"
          :class="[
            donor.id === topDonor?.id ? 'bg-amber-100 text-amber-700' : '',
            isRecentlyAdded(donor.id)
              ? 'ring-2 ring-[#1f9afe] ring-offset-2 scale-110'
              : '',
          ]"
        >
          {{ donor.is_anonymous ? "?" : (donor.name ?? "").charAt(0).toUpperCase() }}
        </div>
        <div class="min-w-0 flex-1">
          <p class="font-semibold text-sm text-gray-900 truncate">
            {{ donor.is_anonymous ? t("ui.commun.anonyme") : donor.name }}
          </p>
          <p class="text-xs text-gray-500">
            <span class="text-sm text-gray-900 ultra-bold">{{
              formatAmountShort(donor.amount)
            }}</span>
            <span class="mx-1">·</span>
            {{ donorBadgeLabel(donor) }}
          </p>
        </div>
      </div>
    </TransitionGroup>

    <div
      v-else-if="isLoadingDonors"
      key="donors-loading"
      class="mt-5 flex items-center justify-center py-4"
    >
      <UIcon
        name="i-lucide-loader-2"
        class="size-5 animate-spin text-gray-400"
      />
    </div>

    <!-- Footer actions -->
    <div
      v-if="donorsTotal > 0"
      class="mt-5 pt-5 border-t border-gray-100 grid grid-cols-2 gap-2"
    >
      <button
        type="button"
        class="py-2.5 rounded-full border border-gray-300 text-sm font-bold text-gray-900 hover:bg-gray-50 transition-colors"
        @click="emit('seeAll')"
      >
        {{ t("ui.donationCard.voirTout") }}
      </button>
      <button
        type="button"
        class="py-2.5 rounded-full border border-gray-300 text-sm font-bold text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5"
        @click="emit('seeTop')"
      >
        <UIcon name="i-lucide-star" class="size-3.5" />
        {{ t("ui.commun.topDons") }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.ultra-bold {
  font-weight: 1000;
  font-stretch: 125%;
  letter-spacing: -0.02em;
  -webkit-text-stroke: 0.5px currentColor;
  text-shadow: 0 0 0.4px currentColor;
}
.amount-pulse {
  animation: amountPulse 0.6s ease-out;
}
@keyframes amountPulse {
  0% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.05);
    color: #1f9afe;
  }
  100% {
    transform: scale(1);
  }
}

/* ─── Animation d'arrivée d'un nouveau donateur (TransitionGroup) ─────────
   Slide depuis le haut + fade + scale subtil. Les items existants
   descendent via FLIP (transform translateY natif Vue TransitionGroup).
   Easing cubic-bezier iOS-like cohérent avec le reste du projet. */
.donor-card-enter-active,
.donor-card-leave-active {
  transition:
    opacity 400ms cubic-bezier(0.32, 0.72, 0, 1),
    transform 400ms cubic-bezier(0.32, 0.72, 0, 1);
}
.donor-card-move {
  transition: transform 400ms cubic-bezier(0.32, 0.72, 0, 1);
}
.donor-card-enter-from {
  opacity: 0;
  transform: translateY(-14px) scale(0.94);
}
.donor-card-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.donor-card-leave-active {
  position: absolute;
}
.donor-card-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.94);
}

/* ─── Highlight "nouveau don" (flash bleu Donoor qui s'estompe en 2s) ───
   La classe est appliquée 3s par le watch + isRecentlyAdded() côté script.
   On utilise un keyframe qui fade le background en 2s pour un effet
   "ping" propre sans laisser de halo permanent. */
.new-donation-highlight {
  animation: new-donation-flash 2s cubic-bezier(0.32, 0.72, 0, 1) forwards;
}
@keyframes new-donation-flash {
  0% {
    background-color: rgba(31, 154, 254, 0.18);
  }
  60% {
    background-color: rgba(31, 154, 254, 0.08);
  }
  100% {
    background-color: transparent;
  }
}
</style>
